'use client'

import { useCookieValue } from '@/Cookies/useCookies'
import {
  selectDisplayPrice,
  VAT_PREFERENCE_COOKIE,
  getDefaultVATPreference,
  type VATPreference,
} from '@/vat/vatUtils'
import type { RegionalBrandCode } from '@/lib/seo/marketUrls'
import { useStorefrontMarket } from '@/useStorefrontMarket'

const getCountryCodeForBrand = (
  brandCode: RegionalBrandCode | null | undefined
) => {
  if (brandCode === 'VANISHING_INC_UK') return 'GB'
  if (brandCode === 'VANISHING_INC_US') return 'US'
  return null
}

export const Price = ({
  price,
  salePrice,
  priceIncVat,
  salePriceIncVat,
  currencySymbol,
  currencyCode,
  taxType,
  size = 'base',
  displayStyle = 'stacked-decimals',
  options,
  amountClassName = 'text-black',
  suffixClassName = 'text-gray-500',
  originalPriceClassName = 'text-gray-500',
  brandCode = null,
}: PriceProps) => {
  const cookieVatPreference = useCookieValue(
    VAT_PREFERENCE_COOKIE
  ) as VATPreference

  const storefrontMarket = useStorefrontMarket({
    fallbackBrand: brandCode,
    currencyCode,
    currencySymbol,
  })
  const enforcedCurrencyCode = storefrontMarket.currencyCode
  const enforcedCurrencySymbol = storefrontMarket.currencySymbol
  const storefrontBrand = storefrontMarket.brandCode

  // Get VAT preference from cookie, or fall back to default based on country
  const countryCode =
    storefrontMarket.countryIsoCode ||
    getCountryCodeForBrand(storefrontBrand) ||
    'US'
  const vatPreference =
    cookieVatPreference || getDefaultVATPreference(countryCode)

  const formatParts = (amount: number | null | undefined) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return null
    }
    const [whole, decimal] = amount.toFixed(2).split('.')
    return { whole, decimal }
  }

  // Size mapping for text classes
  const sizeClasses = {
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-2xl',
  }

  const decimalSizeClasses = {
    sm: 'text-xs',
    base: 'text-xs',
    lg: 'text-sm',
    xl: 'text-base',
  }

  const textClass = sizeClasses[size]
  const decimalClass = decimalSizeClasses[size]

  // Filter out components and options with invalid prices from options
  const validNonComponentOptions =
    options?.filter(option => {
      const productTypeName =
        typeof option.productType === 'string'
          ? option.productType
          : option.productType?.name
      const isComponent = productTypeName?.toLowerCase() === 'component'
      const hasValidPrice =
        option.price !== null &&
        option.price !== undefined &&
        !isNaN(option.price) &&
        option.price > 0
      return !isComponent && hasValidPrice
    }) || []

  // Calculate cheapest price from options if they exist (excluding components and free options)
  // Only show "From" if there are multiple valid options
  const hasMultipleOptions = validNonComponentOptions.length > 1
  const hasOptions = validNonComponentOptions.length > 0
  let effectivePrice = price
  let effectiveSalePrice = salePrice
  let effectivePriceIncVat = priceIncVat
  let effectiveSalePriceIncVat = salePriceIncVat
  let effectiveTaxType = taxType

  const validProvidedSalePrice =
    typeof price === 'number' &&
    typeof salePrice === 'number' &&
    salePrice > 0 &&
    salePrice < price
      ? salePrice
      : null
  const providedEffectivePrice =
    validProvidedSalePrice ??
    (typeof price === 'number' && price > 0 ? price : null)

  if (hasOptions) {
    let lowestEffectivePrice: number | null = null
    let lowestRegularPrice: number | null = null
    let lowestSalePrice: number | null = null
    let lowestPriceOption: {
      priceIncVat?: number | null | undefined
      salePriceIncVat?: number | null | undefined
      taxType?: number | null | undefined
    } | null = null

    for (const option of validNonComponentOptions) {
      const regularPrice = option.price ?? null
      const optionSalePrice = option.salePrice ?? null
      const effectiveOptionPrice =
        optionSalePrice !== null &&
        regularPrice !== null &&
        optionSalePrice < regularPrice &&
        optionSalePrice > 0
          ? optionSalePrice
          : regularPrice

      if (
        effectiveOptionPrice !== null &&
        effectiveOptionPrice > 0 &&
        (lowestEffectivePrice === null ||
          effectiveOptionPrice < lowestEffectivePrice)
      ) {
        lowestEffectivePrice = effectiveOptionPrice
        lowestRegularPrice = regularPrice
        lowestSalePrice =
          optionSalePrice !== null &&
          regularPrice !== null &&
          optionSalePrice < regularPrice &&
          optionSalePrice > 0
            ? optionSalePrice
            : null
        lowestPriceOption = option
      }
    }

    const shouldUseOptionPrice =
      lowestEffectivePrice !== null &&
      (providedEffectivePrice === null ||
        lowestEffectivePrice <= providedEffectivePrice)

    if (
      shouldUseOptionPrice &&
      lowestRegularPrice !== null &&
      lowestRegularPrice > 0
    ) {
      effectivePrice = lowestRegularPrice
      effectiveSalePrice = lowestSalePrice
      effectivePriceIncVat = lowestPriceOption?.priceIncVat ?? priceIncVat
      effectiveSalePriceIncVat =
        lowestPriceOption?.salePriceIncVat ?? salePriceIncVat
      effectiveTaxType = lowestPriceOption?.taxType ?? taxType
    }
  }

  if (
    effectivePrice === null ||
    effectivePrice === undefined ||
    isNaN(effectivePrice)
  ) {
    return null
  }

  const validSalePrice =
    typeof effectiveSalePrice === 'number' &&
    !isNaN(effectiveSalePrice) &&
    effectiveSalePrice < effectivePrice &&
    effectiveSalePrice > 0
      ? effectiveSalePrice
      : null
  const showDiscount = validSalePrice !== null

  const discountPercent = showDiscount
    ? Math.round(((effectivePrice - validSalePrice) / effectivePrice) * 100)
    : 0

  // Select display price using backend-calculated VAT prices with enforced currency
  const mainPriceResult = selectDisplayPrice(
    showDiscount ? validSalePrice : effectivePrice,
    showDiscount ? effectiveSalePriceIncVat : effectivePriceIncVat,
    vatPreference,
    enforcedCurrencyCode, // Use enforced currency code for price logic
    effectiveTaxType
  )

  const originalPriceResult = showDiscount
    ? selectDisplayPrice(
        effectivePrice,
        effectivePriceIncVat,
        vatPreference,
        enforcedCurrencyCode, // Use enforced currency code for price logic
        effectiveTaxType
      )
    : null
  const displaySuffix =
    mainPriceResult.suffix === 'VAT exempt' ? null : mainPriceResult.suffix

  // Format the calculated prices
  const displayMainPrice = formatParts(mainPriceResult.price)
  const displayOriginalPrice = originalPriceResult
    ? formatParts(originalPriceResult.price)
    : null

  if (!displayMainPrice) return null

  return (
    <div
      className="inline-flex items-center gap-2"
      key={`price-${enforcedCurrencySymbol}-${storefrontBrand ?? 'default'}`}
    >
      <span className="sr-only">
        {hasMultipleOptions ? 'From ' : ''}
        {showDiscount && originalPriceResult
          ? `Sale price: ${enforcedCurrencySymbol}${mainPriceResult.price.toFixed(
              2
            )}. Regular price: ${enforcedCurrencySymbol}${originalPriceResult.price.toFixed(
              2
            )}. ${discountPercent}% off.`
          : `Price: ${enforcedCurrencySymbol}${mainPriceResult.price.toFixed(
              2
            )}`}
      </span>

      {mainPriceResult.price === 0 ? (
        <span className={`${textClass} font-semibold ${amountClassName}`}>
          Free
        </span>
      ) : (
        <>
          {/* Main price */}
          <span className={`${textClass} font-semibold ${amountClassName}`}>
            {hasMultipleOptions && (
              <span className="text-gray-600 font-normal mr-1">From</span>
            )}
            {displayStyle === 'inline' ? (
              <>
                {enforcedCurrencySymbol}
                {mainPriceResult.price.toFixed(2)}
              </>
            ) : (
              <>
                {enforcedCurrencySymbol}
                {displayMainPrice.whole}
                <span className={`${decimalClass} relative -top-1`}>
                  .{displayMainPrice.decimal}
                </span>
              </>
            )}
            {displaySuffix && (
              <span className={`text-xs ${suffixClassName} font-normal ml-1`}>
                {displaySuffix}
              </span>
            )}
          </span>

          {/* Struck-through original price */}
          {showDiscount && displayOriginalPrice && (
            <span className={`${textClass} ${originalPriceClassName}`}>
              {displayStyle === 'inline' ? (
                <span className="line-through">
                  {enforcedCurrencySymbol}
                  {originalPriceResult?.price.toFixed(2)}
                </span>
              ) : (
                <>
                  <span className="line-through">
                    {enforcedCurrencySymbol}
                    {displayOriginalPrice.whole}
                  </span>
                  <span className={`${decimalClass} relative -top-1`}>
                    .{displayOriginalPrice.decimal}
                  </span>
                </>
              )}
            </span>
          )}

          {/* Discount badge */}
          {showDiscount && (
            <span className="inline-block text-center leading-tight text-xs font-semibold text-white bg-green-600 px-1.5 py-0.5 rounded">
              {discountPercent}% OFF
            </span>
          )}
        </>
      )}
    </div>
  )
}

interface PriceProps {
  price: number | null | undefined
  salePrice?: number | null
  priceIncVat?: number | null
  salePriceIncVat?: number | null
  currencySymbol: string
  currencyCode?: string | null
  taxType?: number | null
  size?: 'sm' | 'base' | 'lg' | 'xl'
  displayStyle?: 'stacked-decimals' | 'inline'
  amountClassName?: string
  suffixClassName?: string
  originalPriceClassName?: string
  brandCode?: RegionalBrandCode | null
  options?: Array<{
    price: number | null | undefined
    salePrice?: number | null | undefined
    priceIncVat?: number | null | undefined
    salePriceIncVat?: number | null | undefined
    taxType?: number | null | undefined
    productType?:
      | string
      | {
          name?: string | null
        }
      | null
  }>
}
